import React, { useMemo, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {FaCheck, FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";
import {addDays, isAfter, isBefore, startOfDay} from "date-fns";
import InfoGrid from "../../../../../components/info-grid/info-grid.jsx";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import ProjectUtils from "../../../../../utils/project-utils.js";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import PrintableTableAgGrid from "../../../../../components/printable-table/printable-table-ag-grid.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const formatDate = (value) => value ? ProjectUtils.formatDate(value, "DD/MM/YYYY") : null;

const getDueDate = (value) => ProjectUtils.toDate(value, "dd/MM/yyyy");

const isAlmostExpired = (value) => {
    const date = getDueDate(value);
    const today = startOfDay(new Date());

    return date && !isBefore(startOfDay(date), today) && !isAfter(startOfDay(date), addDays(today, 45));
};

const isExpired = (value) => {
    const date = getDueDate(value);

    return date && isBefore(startOfDay(date), startOfDay(new Date()));
};

const SectionTitle = ({children}) => (
    <section className="px-5!">
        <h2 className="w-full rounded-[5px] border border-[#e0e0e0] bg-[#efefef] py-0! text-center text-lg! text-[#4f4f4f] print:text-[19px]! print:break-after-avoid!">
            {children}
        </h2>
    </section>
);

const ReportContent = ({data}) => {
    const requester = data?.requester ?? {};
    const cautions = data?.cautions ?? [];
    const [gridApi, setGridApi] = useState(null);
    const categoryCnh = requester.categoryCnh?.join(", ") ?? "";

    const getDocumentItem = (label, value) => {
        const expired = isExpired(value);
        const almostExpired = isAlmostExpired(value);
        const formattedValue = formatDate(value);

        return {
            label,
            value: formattedValue ?? "",
            labelClassName: `${almostExpired ? "!text-[#fc6d00]" : expired || !formattedValue ? "!text-[#dc4c64]" : ""}`,
            children: (
                <>
                    <span className={`inline-flex items-center gap-1 ${almostExpired ? "!text-[#fc6d00]" : expired || !formattedValue ? "!text-[#dc4c64]" : ""}`}>
                        <span className={`${!formattedValue ? "italic" : ""}`}>{ formattedValue ?? "Não informado" }</span>

                        { almostExpired ? (
                            <FaCircleExclamation title="A vencer" className={'transform translate-y-[-1px]'}/>
                        ) : null}

                        { expired ? (
                            <FaTriangleExclamation title="Vencido" className={'transform translate-y-[-1px]'}/>
                        ) : null }

                        { !formattedValue ? (
                            <FaTriangleExclamation title="Não possui" className={'transform translate-y-[-1px]'}/>
                        ) : null }

                        { label === 'CNH' && categoryCnh ? (
                            <span className={`text-[12px] ml-[7px]! bg-[#e8f0fe] text-[#1967D2] rounded px-1`} title={`Categoria: ${categoryCnh}`}>
                                Cat. {categoryCnh}
                            </span>
                        ) : null}
                    </span>
                </>
            ),
        };
    };

    const requesterInfo = [
        {
            label: "Nome",
            value: requester.name ?? "",
        },
        {
            label: "Nome de guerra",
            value: requester.namewar ?? ""
        },
        {
            label: "CNH",
            ...getDocumentItem("CNH", requester.expirationCnh)
        },
        {
            label: "Porte de arma",
            ...getDocumentItem("Porte de arma", requester.carryDueDate)
        },
        {
            label: "Exame de tiro",
            ...getDocumentItem("Exame de tiro", requester.shotDueDate)
        },
        {
            label: "Exame psicológico",
            ...getDocumentItem("Exame psicológico", requester.psychoDueDate)
        },
    ];

    const columnDefs = useMemo(() => [
        {headerName: "Entrega", field: "date", flex: 1, width: 140, sort: "desc", tooltipField: "date"},
        {headerName: "Depósito", field: "storage_description", flex: 1, sortable: false, comparator: aggridUtils.lowerUpCaseComparator, tooltipField: "storage_description"},
        {headerName: "Material", field: "material_description", flex: 1, sortable: false, comparator: aggridUtils.lowerUpCaseComparator, tooltipField: "material_description"},
        {
            headerName: "Permanente", field: "permanent", sortable: false, flex: 1, width: 60, tooltipValueGetter: ({ data: { permanent } }) => permanent ? "Sim" : "",
            cellRenderer: ({data: caution}) => caution.permanent ? (
                <div className="flex h-full w-full items-center justify-center"><FaCheck color="green"/></div>
            ) : null,
        },
        {headerName: "Quantidade", field: "quantity", sortable: false, flex: 1, width: 100, tooltipField: "quantity"},
        {headerName: "Observação", field: "observations", flex: 1, sortable: false, width: 280, tooltipField: "observations"},
        {headerName: "Avaria", field: "breakdown", sortable: false, width: 200, tooltipField: "breakdown"},
    ], []);

    const defaultColDef = useMemo(() => ({
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        suppressFloatingFilterButton: true,
    }), []);

    const userImage = requester.id ? `${API_URL}/user/getImage/${requester.id}` : getAssetPath("img/user.png");

    return (
        <div className="space-y-4">
            <SectionTitle>SOLICITANTE</SectionTitle>
            <div className="grid grid-cols-12 gap-4 px-5">
                <div className="col-span-2 h-[137px] border border-[#e0e0e0] bg-white p-1 shadow-sm">
                    <img
                        src={userImage}
                        alt="Solicitante"
                        className="h-full w-full object-contain"
                        onError={({currentTarget}) => {
                            currentTarget.onerror = null;
                            currentTarget.src = getAssetPath("img/user.png");
                        }}
                    />
                </div>

                <InfoGrid title="" items={requesterInfo} columns={2} className="col-span-10 px-0!" labelClassName="text-[#4F4F4F] text-[13px]! print:text-[15px]!" valueClassName="print:text-[17px]!" contentClassName="space-y-1"/>
            </div>

            <SectionTitle>MATERIAIS EM CAUTELA</SectionTitle>
            <div className="px-5">
                <div className="min-h-[74px] print:hidden">
                    <AgGridReact
                        rowData={cautions}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressCellFocus={true}
                        gridOptions={{localeText: aggridUtils.localization()}}
                        theme={aggridUtils.generalTheme({noHover: true})}
                        domLayout="autoHeight"
                        onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                        onGridReady={({api}) => setGridApi(api)}
                        tooltipShowDelay={400}
                        tooltipShowMode='whenTruncated'
                    />
                </div>

                <div className="hidden overflow-hidden rounded-[8px] border border-[#e0e1e3] print:block">
                    <PrintableTableAgGrid
                        formatters={{
                            permanent: (caution) => caution.permanent ? (
                                <>
                                    <div className="flex h-full w-full items-center justify-center"><FaCheck color="green"/></div>
                                </>
                            ) : ""
                        }}
                        gridApi={gridApi}
                        className="w-full -my-px border-0!"
                        thClassName="first:!border-l-0 last:!border-r-0 text-[#676a6c]"
                        tdClassName="first:!border-l-0 last:!border-r-0"
                    />
                </div>
            </div>
        </div>
    );
};

export default ReportContent;
